import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {z} from 'zod';
import {SeedQuestion, SeedQuestionStatus} from '@cp/model';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import usePagination from '../../../hooks/helper/usePagination';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';
import {useListSeedQuestions} from '../../../hooks/seedQuestion/useListSeedQuestions';
import {useCountSeedQuestionsFetch} from '../../../hooks/seedQuestion/useCountSeedQuestions';

const seedQuestionsParamsSchema = z.object({
  moduleId: z.string().optional(),
  topic: z.string().optional(),
  status: z.nativeEnum(SeedQuestionStatus).optional(),
  page: z.number().int().positive().optional(),
});

type SeedQuestionsParams = z.infer<typeof seedQuestionsParamsSchema>;

export interface UseAdminSeedQuestionsPageOptions {
  defaultFiltersExpanded?: boolean;
}

export default function useAdminSeedQuestionsPage(options?: UseAdminSeedQuestionsPageOptions) {
  const {defaultFiltersExpanded = false} = options || {};
  const navigate = useNavigate();
  const location = useLocation();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {
    seedQuestions,
    loading: seedQuestionsLoading,
    fetchSeedQuestions,
    refetchSeedQuestions,
  } = useListSeedQuestions();
  const {showMessage} = useMessage();

  const {searchParams, setSearchParam, setSearchParams} = useSearchParamsHandler<SeedQuestionsParams>({
    schema: seedQuestionsParamsSchema,
    defaultValues: {
      moduleId: '',
      topic: '',
      status: undefined,
      page: 1,
    },
  });

  const moduleId = searchParams.moduleId || '';
  const topic = searchParams.topic || '';
  const status = searchParams.status;
  const urlPage = searchParams.page || 1;

  const [filtersExpanded, setFiltersExpanded] = useState(defaultFiltersExpanded);
  const lastUrlPageRef = useRef<number>(urlPage);
  const hasInitializedRef = useRef<boolean>(false);
  const hasFetchedRef = useRef<boolean>(false);
  const refreshHandledRef = useRef<boolean>(false);

  const selectedModule = examModules.find((m) => m.id === moduleId);

  // Memoize the topics and statuses arrays to prevent unnecessary count fetches
  const topicsArray = useMemo(() => (topic ? [topic] : undefined), [topic]);
  const statusesArray = useMemo(() => (status ? [status] : undefined), [status]);

  // Get total count based on current filters
  const totalItems = useCountSeedQuestionsFetch(moduleId || undefined, topicsArray, statusesArray);

  const availableTopics = useMemo(() => {
    if (!selectedModule) return [];
    return selectedModule.topics;
  }, [selectedModule]);

  // Validate topic value against available topics and return safe value for dropdown
  const validatedTopic = useMemo(() => {
    if (!topic) return ''; // Empty topic is valid (means "All")
    if (!selectedModule || availableTopics.length === 0) return ''; // Return empty if module/topics not loaded yet
    // Only return topic if it exists in availableTopics
    return availableTopics.some((t) => t.topic === topic) ? topic : '';
  }, [topic, selectedModule, availableTopics]);

  const buildListVariables = useCallback(
    (page: number, pageSize: number) => {
      const startIndex = (page - 1) * pageSize;
      return {
        moduleId,
        topic: topic || undefined,
        status: status || undefined,
        limit: pageSize,
        offset: startIndex,
      };
    },
    [moduleId, topic, status],
  );

  // Create a stable fetch function that uses current filter values
  const fetchWithCurrentFilters = useCallback(
    (page: number, pageSize: number) => {
      if (!moduleId) return;
      const variables = buildListVariables(page, pageSize);
      return fetchSeedQuestions(variables).catch((error) => {
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return;
        }
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch seed questions';
        showMessage(errorMessage, 3000, 'error');
      });
    },
    [moduleId, fetchSeedQuestions, showMessage, buildListVariables],
  );

  const pagination = usePagination({
    pageSize: 10,
    totalItems,
    initialPage: urlPage,
    handlePageChange: (_startIndex, _endIndex, currentPageSize, currentPage) => {
      // Skip the initial mount trigger - we don't want to auto-fetch on page load
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        return;
      }
      // Only fetch if we have a moduleId and this is a real page change (not initial mount)
      if (moduleId && hasFetchedRef.current) {
        lastUrlPageRef.current = currentPage;
        setSearchParam('page', currentPage);
        fetchWithCurrentFilters(currentPage, currentPageSize);
      }
    },
  });

  // Reset topic if it's invalid when module/topics load
  useEffect(() => {
    if (topic && selectedModule && availableTopics.length > 0 && validatedTopic === '') {
      setSearchParam('topic', '');
    }
  }, [topic, selectedModule, availableTopics, validatedTopic, setSearchParam]);

  // Sync pagination with URL page parameter when URL changes externally (e.g., browser back/forward)
  // We use a ref to track the last page we set ourselves to avoid loops
  useEffect(() => {
    // Skip if this URL change was caused by us updating the page
    if (urlPage === lastUrlPageRef.current) {
      return;
    }

    // Only sync if URL page is different from current page and we've already initialized
    // This handles browser back/forward navigation, but not initial load
    if (hasInitializedRef.current && urlPage !== pagination.currentPage && urlPage >= 1) {
      // Only update if the page is valid (within total pages or reasonable range)
      const maxPage = pagination.totalPages || 100; // Use a reasonable max if totalPages not set yet
      if (urlPage <= maxPage) {
        lastUrlPageRef.current = urlPage;
        pagination.gotoPage(urlPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage]); // Only depend on urlPage to avoid loops

  const handleModuleChange = useCallback(
    (newModuleId: string) => {
      setSearchParams({
        moduleId: newModuleId,
        topic: '',
        status: undefined,
        page: 1,
      });
      pagination.gotoPage(1);
      setFiltersExpanded(true);
    },
    [pagination, setSearchParams],
  );

  const handleTopicChange = useCallback(
    (newTopic?: string) => {
      const topicValue = newTopic || '';
      setSearchParams({
        topic: topicValue,
        page: 1,
      });
      pagination.gotoPage(1);
    },
    [pagination, setSearchParams],
  );

  const handleStatusChange = useCallback(
    (newStatus?: SeedQuestionStatus) => {
      setSearchParams({
        status: newStatus,
        page: 1,
      });
      pagination.gotoPage(1);
    },
    [pagination, setSearchParams],
  );

  const handleFetchSeedQuestions = useCallback(async () => {
    if (!moduleId) {
      showMessage('Please select a module', 3000, 'error');
      return;
    }

    try {
      hasFetchedRef.current = true;
      // Reset to page 1 and fetch
      setSearchParam('page', 1);
      pagination.gotoPage(1);
      // Explicitly refetch to ensure we hit the network (not cache)
      await refetchSeedQuestions(buildListVariables(1, pagination.pageSize));
      setFiltersExpanded(false);
    } catch (error) {
      if ((error as Error)?.name === 'AbortError' || (error as Error)?.message?.includes('aborted')) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch seed questions';
      showMessage(errorMessage, 3000, 'error');
    }
  }, [moduleId, showMessage, pagination, setSearchParam, refetchSeedQuestions, buildListVariables]);

  const handleSeedQuestionClick = useCallback(
    (seedQuestion: SeedQuestion) => {
      // Build URL with query parameters
      const params = new URLSearchParams();
      if (moduleId) params.set('moduleId', moduleId);
      if (topic) params.set('topic', topic);
      if (status) params.set('status', status);
      if (pagination.currentPage > 1) params.set('page', String(pagination.currentPage));
      const queryString = params.toString();
      const url = `/admin/seedQuestion/${seedQuestion.id}${queryString ? `?${queryString}` : ''}`;

      navigate(url, {
        state: {
          seedQuestion, // Keep seedQuestion in state for initial render performance
        },
      });
    },
    [navigate, moduleId, topic, status, pagination.currentPage],
  );

  // Handle page change from pagination component
  // Just update pagination state - usePagination's handlePageChange will update URL and fetch
  const handlePageChange = useCallback(
    (page: number) => {
      // Mark that we've fetched so future changes will trigger fetches
      hasFetchedRef.current = true;
      pagination.gotoPage(page);
      // The handlePageChange callback in usePagination will:
      // 1. Update the URL via setSearchParam
      // 2. Trigger the fetch via fetchWithCurrentFilters
    },
    [pagination],
  );

  // Auto-fetch when returning from detail page
  useEffect(() => {
    const locationState = location.state as {refresh?: boolean} | undefined;
    if (locationState?.refresh && moduleId && hasFetchedRef.current && !refreshHandledRef.current) {
      refreshHandledRef.current = true;
      // Clear the refresh flag from state by navigating with replace
      navigate(location.pathname + location.search, {replace: true, state: undefined});
      // Use the page from URL to ensure we're on the correct page
      const pageToFetch = urlPage || pagination.currentPage;
      // Sync pagination if URL page is different
      if (urlPage !== pagination.currentPage && urlPage >= 1) {
        lastUrlPageRef.current = urlPage;
        pagination.gotoPage(urlPage);
      }
      // Trigger a refetch with the page from URL
      refetchSeedQuestions(buildListVariables(pageToFetch, pagination.pageSize));
    } else if (!locationState?.refresh) {
      // Reset the flag when there's no refresh state
      refreshHandledRef.current = false;
    }
  }, [
    location.state,
    location.pathname,
    location.search,
    moduleId,
    pagination,
    refetchSeedQuestions,
    navigate,
    urlPage,
    buildListVariables,
  ]);

  return {
    examModules,
    seedQuestions,
    moduleId,
    topic,
    validatedTopic, // Safe topic value for dropdown (validated against availableTopics)
    status,
    availableTopics,
    selectedModule,
    modulesLoading,
    seedQuestionsLoading,
    filtersExpanded,
    setFiltersExpanded,
    pagination,
    totalItems,
    handleModuleChange,
    handleTopicChange,
    handleStatusChange,
    handleFetchSeedQuestions,
    handleSeedQuestionClick,
    handlePageChange,
  };
}
